import { useEffect, useMemo, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState, cacheGet } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Landmark, Plus, Building2, ArrowRight, Eye, Pencil, Trash2, Users2, Search, LayoutGrid, List } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NoAccess, PORTFOLIO_ADMIN_ROLES } from "@/components/NoAccess";

// Red · yellow · green counts for one portfolio — click goes straight to that portfolio's Pulse.
function PulseCounts({ company, onOpen, compact = false }) {
  const p = company.pulse;
  if (!p) return null;
  const scored = p.red + p.yellow + p.green + p.quiet;
  if (!scored && !p.off) return null;
  const dot = (color) => <i className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: color }} />;
  return (
    <button type="button" data-testid={`company-pulse-${company.id}`} onClick={(e) => { e.stopPropagation(); onOpen(); }} title="Open this portfolio's Pulse"
      className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-bold rounded-full border border-sand-200 bg-sand-50/60 hover:border-ink px-2.5 py-1 transition-colors ${compact ? "" : "mt-2"}`}>
      <span className="text-ink/45 font-extrabold uppercase tracking-[0.06em] text-[9.5px]">Pulse</span>
      {p.red > 0 && <span className="text-[#B91C1C] inline-flex items-center gap-1">{dot("#D32F2F")}{p.red}</span>}
      {p.yellow > 0 && <span className="text-[#B45309] inline-flex items-center gap-1">{dot("#D97706")}{p.yellow}</span>}
      {p.green > 0 && <span className="text-[#15803D] inline-flex items-center gap-1">{dot("#16A34A")}{p.green}</span>}
      {p.quiet > 0 && <span className="text-ink/45 inline-flex items-center gap-1">{dot("#CBD5E1")}{p.quiet} quiet</span>}
      {scored === 0 && <span className="text-ink/45">not on yet</span>}
      {scored > 0 && p.off > 0 && <span className="text-ink/35">· {p.off} off</span>}
    </button>
  );
}

export default function ManagementCompanies() {
  const { role, enterCompanyView } = useDash();
  const [companies, setCompanies] = useCachedState("companies", []);
  const [loading, setLoading] = useState(() => cacheGet("companies") === undefined);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name} being renamed
  const [delTarget, setDelTarget] = useState(null); // company pending typed-confirmation delete
  const [delText, setDelText] = useState("");
  const [delMembers, setDelMembers] = useState(false); // opt-in: remove its team with it
  const [deleting, setDeleting] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSortState] = useState(() => localStorage.getItem("askredline.portfoliosSort") || "name");
  const setSort = (v) => { setSortState(v); try { localStorage.setItem("askredline.portfoliosSort", v); } catch { /* ignore */ } };
  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem("askredline.portfoliosView") || "cards"; } catch { return "cards"; }
  });
  const setView = (v) => { setViewState(v); try { localStorage.setItem("askredline.portfoliosView", v); } catch { /* ignore */ } };

  const load = useCallback(() => api.get("/companies").then((r) => setCompanies(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => ({
    portfolios: companies.length,
    communities: companies.reduce((a, c) => a + (c.communities || 0), 0),
    members: companies.reduce((a, c) => a + (c.members || 0), 0),
  }), [companies]);

  const list = useMemo(() => {
    let l = [...companies];
    const s = q.trim().toLowerCase();
    if (s) l = l.filter((c) => (c.name || "").toLowerCase().includes(s));
    if (sort === "name") l.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "communities") l.sort((a, b) => (b.communities || 0) - (a.communities || 0));
    if (sort === "members") l.sort((a, b) => (b.members || 0) - (a.members || 0));
    // Trouble floats to the top: most red, then most yellow, then most scored; A→Z as the tiebreaker.
    if (sort === "pulse") l.sort((a, b) => {
      const pa = a.pulse || {}, pb = b.pulse || {};
      return (pb.red || 0) - (pa.red || 0) || (pb.yellow || 0) - (pa.yellow || 0)
        || ((pb.green || 0) + (pb.quiet || 0)) - ((pa.green || 0) + (pa.quiet || 0)) || (a.name || "").localeCompare(b.name || "");
    });
    return l;
  }, [companies, q, sort]);

  if (!PORTFOLIO_ADMIN_ROLES.includes(role)) return <NoAccess page="Portfolios" />;

  const create = async () => {
    if (!name.trim()) { toast.error("Portfolio name required"); return; }
    setSaving(true);
    try {
      await api.post("/companies", { name: name.trim() });
      toast.success("Portfolio created");
      setName(""); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveRename = async () => {
    if (!editing?.name.trim()) { toast.error("Portfolio name required"); return; }
    try {
      await api.put(`/companies/${editing.id}`, { name: editing.name.trim() });
      toast.success("Renamed"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = (c) => {
    if (c.communities > 0) { toast.error(`Unassign its ${c.communities} communit${c.communities === 1 ? "y" : "ies"} first`); return; }
    setDelTarget(c); setDelText(""); setDelMembers(false);
  };

  const doDelete = async () => {
    if (!delTarget || delText.trim() !== delTarget.name) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/companies/${delTarget.id}${delMembers ? "?remove_members=true" : ""}`);
      const n = r.data?.members_removed || 0;
      toast.success(`Portfolio deleted${n ? ` · ${n} team member${n === 1 ? "" : "s"} removed` : ""}`);
      setDelTarget(null); setDelText(""); setDelMembers(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="management-companies">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <PageTitle title={<><Landmark className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Portfolios</>} about="Each portfolio is a management firm's book of communities. View any portfolio exactly as their Administrator sees it, or manage their team." />
        </div>
        <button data-testid="new-company-btn" onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors">
          <Plus className="h-4 w-4" /> New portfolio
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : companies.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sand-200 p-10 text-center text-ink/50" data-testid="companies-empty">
          No portfolios yet. Create one, then tag communities to it in the community wizard.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Rollup */}
          <div data-testid="portfolios-rollup" className="bg-white rounded-2xl border border-sand-200 px-5 py-3 flex flex-wrap items-center gap-x-7 gap-y-2">
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-xl font-extrabold tracking-tight text-ink">{totals.portfolios}</span>
              <span className="text-xs text-ink/50 font-medium">Portfolios</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-xl font-extrabold tracking-tight text-ink/70">{totals.communities}</span>
              <span className="text-xs text-ink/50 font-medium">Communities managed</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-xl font-extrabold tracking-tight text-ink/70">{totals.members}</span>
              <span className="text-xs text-ink/50 font-medium">Team members</span>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="portfolios-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search portfolios…"
                className="rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink w-56" />
            </div>
            <select data-testid="portfolios-sort" value={sort} onChange={(e) => setSort(e.target.value)}
              className="rounded-full bg-white border border-sand-200 px-3.5 py-2 text-sm font-semibold text-ink/70 outline-none">
              <option value="name">Sort: Name</option>
              <option value="communities">Sort: Most communities</option>
              <option value="members">Sort: Most members</option>
              <option value="pulse">Sort: Pulse — most red first</option>
            </select>
            <div className="flex rounded-full border border-sand-200 bg-white p-0.5 ml-auto" data-testid="portfolios-view-toggle">
              <button data-testid="portfolios-view-cards-btn" onClick={() => setView("cards")} title="Card view"
                className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "cards" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
                <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
              </button>
              <button data-testid="portfolios-view-list-btn" onClick={() => setView("list")} title="List view"
                className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
                <List className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {view === "list" ? (
            <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden" data-testid="portfolios-list">
              {list.map((c) => (
                <div key={c.id} data-testid={`company-row-${c.id}`} className="group flex items-center gap-3 px-4 py-3 hover:bg-sand-50/70 transition-colors">
                  <div className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Landmark className="h-4.5 w-4.5 text-indigo-600" strokeWidth={1.5} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-sm text-ink truncate">{c.name}</div>
                    <div className="text-xs text-ink/50 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>{c.communities} communit{c.communities === 1 ? "y" : "ies"} · {c.members || 0} member{(c.members || 0) === 1 ? "" : "s"}</span>
                      <PulseCounts company={c} compact onOpen={() => enterCompanyView({ id: c.id, name: c.name }, "/dashboard/portfolio-pulse")} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`rename-company-${c.id}`} onClick={() => setEditing({ id: c.id, name: c.name })}
                      className="h-8 w-8 rounded-lg text-ink/40 hover:text-ink hover:bg-sand-100 flex items-center justify-center transition-colors" title="Rename">
                      <Pencil className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                    <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c)}
                      className="h-8 w-8 rounded-lg text-ink/40 hover:text-redline-500 hover:bg-redline-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title={c.communities > 0 ? "Unassign its communities first" : "Delete"}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                    <button data-testid={`view-portfolio-${c.id}`} onClick={() => enterCompanyView({ id: c.id, name: c.name })}
                      className="rounded-full bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" /> View as admin
                    </button>
                  </div>
                </div>
              ))}
              {list.length === 0 && <div className="text-center py-14 text-ink/40">No portfolios match your search.</div>}
            </div>
          ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => (
            <div key={c.id} data-testid={`company-card-${c.id}`} className="bg-white rounded-2xl border border-sand-200 p-5">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center"><Landmark className="h-5 w-5 text-indigo-600" strokeWidth={1.5} /></div>
                <div className="flex items-center gap-1">
                  <button data-testid={`rename-company-${c.id}`} onClick={() => setEditing({ id: c.id, name: c.name })}
                    className="h-8 w-8 rounded-lg text-ink/40 hover:text-ink hover:bg-sand-100 flex items-center justify-center transition-colors" title="Rename">
                    <Pencil className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c)}
                    className="h-8 w-8 rounded-lg text-ink/40 hover:text-redline-500 hover:bg-redline-50 flex items-center justify-center transition-colors disabled:opacity-40"
                    title={c.communities > 0 ? "Unassign its communities first" : "Delete"}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
              <div className="font-heading font-bold text-ink mt-3 truncate">{c.name}</div>
              <div className="flex items-center gap-3 mt-1 text-xs text-ink/60">
                <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {c.communities} communit{c.communities === 1 ? "y" : "ies"}</span>
                <span className="flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> {c.members || 0} member{(c.members || 0) === 1 ? "" : "s"}</span>
              </div>
              <PulseCounts company={c} onOpen={() => enterCompanyView({ id: c.id, name: c.name }, "/dashboard/portfolio-pulse")} />
              <button data-testid={`view-portfolio-${c.id}`} onClick={() => enterCompanyView({ id: c.id, name: c.name })}
                className="mt-4 w-full flex items-center justify-center gap-1.5 rounded-full bg-ink text-white font-semibold text-sm py-2.5 hover:bg-ink-700 transition-colors">
                <Eye className="h-4 w-4" /> View as portfolio admin <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {list.length === 0 && <div className="col-span-full text-center py-14 text-ink/40">No portfolios match your search.</div>}
          </div>
          )}
        </div>
      )}

      {/* Delete company — typed confirmation, same guardrail as community deletion */}
      <Dialog open={!!delTarget} onOpenChange={(v) => { if (!v) { setDelTarget(null); setDelText(""); setDelMembers(false); } }}>
        <DialogContent className="max-w-md" data-testid="delete-company-dialog">
          <DialogHeader><DialogTitle className="font-heading text-danger-600 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Delete portfolio</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-ink-700">
              This permanently deletes <span className="font-bold">{delTarget?.name}</span>. This <span className="font-semibold">cannot be undone</span>.
            </p>
            {(delTarget?.members || 0) > 0 && (
              <label data-testid="delete-company-members-row" className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-sand-50 p-3 cursor-pointer">
                <input type="checkbox" data-testid="delete-company-members" checked={delMembers} onChange={(e) => setDelMembers(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-redline-500" />
                <span className="text-sm text-ink-700">
                  <span className="font-semibold">Also remove its {delTarget.members} team member{delTarget.members === 1 ? "" : "s"}</span>
                  <span className="block text-xs text-ink/60 mt-0.5">Their sign-in is deleted with the portfolio. Leave this off and the delete is refused while anyone is still on the team.</span>
                </span>
              </label>
            )}
            <div>
              <label className="text-sm font-semibold">Type the portfolio name to confirm</label>
              <input data-testid="delete-company-confirm-input" value={delText} onChange={(e) => setDelText(e.target.value)}
                placeholder={delTarget?.name || ""} autoFocus
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setDelTarget(null); setDelText(""); setDelMembers(false); }}
              className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="delete-company-confirm-btn" onClick={doDelete}
              disabled={deleting || delText.trim() !== (delTarget?.name || "") || ((delTarget?.members || 0) > 0 && !delMembers)}
              className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {deleting ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="new-company-dialog">
          <DialogHeader><DialogTitle className="font-heading">New portfolio</DialogTitle></DialogHeader>
          <label className="text-sm font-semibold">Portfolio / firm name</label>
          <input data-testid="new-company-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Summit Community Management" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="new-company-save" onClick={create} disabled={saving}
            className="mt-3 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">
            {saving ? "Creating…" : "Create"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="rename-company-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rename portfolio</DialogTitle></DialogHeader>
          <label className="text-sm font-semibold">Portfolio / firm name</label>
          <input data-testid="rename-company-name" value={editing?.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="rename-company-save" onClick={saveRename}
            className="mt-3 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
